import { useState, useCallback, useMemo } from 'react';

const useEnhancedPagination = ({
  initialPageSize = 25,
  defaultSortBy = 'created_at',
  defaultSortOrder = 'desc',
  enableVirtualization = true,
  virtualizationThreshold = 50
}) => {
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: initialPageSize,
    total_count: 0,
    total_pages: 0
  });

  const [sorting, setSorting] = useState({
    orderBy: defaultSortBy,
    order: defaultSortOrder
  });

  const [virtualizationEnabled, setVirtualizationEnabled] = useState(enableVirtualization);

  // Determine if virtualization should be used
  const shouldUseVirtualization = useMemo(() => {
    return virtualizationEnabled && pagination.total_count > virtualizationThreshold;
  }, [virtualizationEnabled, pagination.total_count, virtualizationThreshold]);

  // Handle page changes
  const handlePageChange = useCallback((event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  }, []);

  // Handle page size changes
  const handlePageSizeChange = useCallback((newPageSize) => {
    setPagination(prev => ({
      ...prev,
      page: 1, // Reset to first page when changing page size
      page_size: newPageSize
    }));
  }, []);

  // Handle sort changes
  const handleSortChange = useCallback((newSorting) => {
    setSorting(newSorting);
    // Reset to first page when sorting changes
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  }, []);

  // Update pagination data from API response
  const updatePaginationData = useCallback((apiResponse) => {
    if (apiResponse?.pagination) {
      setPagination(prev => ({
        ...prev,
        ...apiResponse.pagination
      }));
    }
  }, []);

  // Reset pagination to initial state
  const resetPagination = useCallback(() => {
    setPagination({
      page: 1,
      page_size: initialPageSize,
      total_count: 0,
      total_pages: 0
    });
    setSorting({
      orderBy: defaultSortBy,
      order: defaultSortOrder
    });
  }, [initialPageSize, defaultSortBy, defaultSortOrder]);

  // Get current pagination parameters for API calls
  const getPaginationParams = useCallback(() => {
    return {
      page: pagination.page,
      page_size: pagination.page_size,
      sort_by: sorting.orderBy,
      sort_order: sorting.order
    };
  }, [pagination.page, pagination.page_size, sorting.orderBy, sorting.order]);

  // Calculate display information
  const paginationInfo = useMemo(() => {
    const startItem = (pagination.page - 1) * pagination.page_size + 1;
    const endItem = Math.min(pagination.page * pagination.page_size, pagination.total_count);
    
    return {
      startItem,
      endItem,
      totalItems: pagination.total_count,
      currentPage: pagination.page,
      totalPages: pagination.total_pages,
      pageSize: pagination.page_size,
      hasNextPage: pagination.page < pagination.total_pages,
      hasPreviousPage: pagination.page > 1
    };
  }, [pagination]);

  return {
    // State
    pagination,
    sorting,
    virtualizationEnabled,
    shouldUseVirtualization,
    paginationInfo,
    
    // Actions
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    updatePaginationData,
    resetPagination,
    getPaginationParams,
    setVirtualizationEnabled,
    
    // Utils
    isFirstPage: pagination.page === 1,
    isLastPage: pagination.page === pagination.total_pages,
    isEmpty: pagination.total_count === 0
  };
};

export default useEnhancedPagination;